<script setup lang="ts">
import { Form, Person } from '~/layers/forms/models'
import { requiredValidator } from '~/utils/validators'

interface Props {
  modelValue: Omit<Person, 'identification'>
  insurable?: Insurable | null
  validationScope?: string
  prefill?: 'all' | (keyof Person)[] | false
}

const props = withDefaults(defineProps<Props>(), {
  insurable: null,
  validationScope: Form.customer,
  prefill: false,
})

const emits = defineEmits(['update:modelValue'])
const model = useVModel(props, 'modelValue', emits)

const { constraints } = useContractConstraints()

const v$ = useValidation(
  () => ({
    name: { required: requiredValidator },
    firstLastName: { required: requiredValidator },
    birthDate: { required: requiredValidator },
    gender: { required: requiredValidator },
  }),
  model,
  {
    $scope: props.validationScope ?? Form.customer,
    $registerAs: props.validationScope ?? Form.customer,
  }
)

const isPrefilled = (key: keyof Person) => {
  if (props.prefill === 'all') return true

  if (Array.isArray(props.prefill) && props.prefill.includes(key)) return true

  return false
}
</script>

<template>
  <InputField
    label="Nombre"
    :error="!isPrefilled('name') && v$.name?.$error"
    :error-message="v$.name?.$errors[0]?.$message.toString()"
    class="sm:col-span-full md:col-span-1"
  >
    <PvInputText
      v-model="model.name"
      :disabled="isPrefilled('name')"
    />
  </InputField>

  <InputField
    label="Primer apellido"
    :error="!isPrefilled('firstLastName') && v$.firstLastName?.$error"
    :error-message="v$.firstLastName?.$errors[0]?.$message.toString()"
  >
    <PvInputText
      v-model="model.firstLastName"
      :disabled="isPrefilled('firstLastName')"
    />
  </InputField>

  <InputField label="Segundo apellido">
    <PvInputText
      v-model="model.secondLastName"
      :disabled="isPrefilled('secondLastName')"
    />
  </InputField>

  <InputField
    label="Fecha de nacimiento"
    :error="!isPrefilled('birthDate') && v$.birthDate?.$error"
    :error-message="v$.birthDate?.$errors[0]?.$message.toString()"
  >
    <PvDatePicker
      v-model="model.birthDate"
      :max-date="constraints?.maxInsuredBirthDate"
      :min-date="constraints?.minInsuredBirthDate"
      :disabled="isPrefilled('birthDate')"
    />
  </InputField>

  <InputField
    label="Sexo"
    :error="!isPrefilled('gender') && v$.gender?.$error"
    :error-message="v$.gender?.$errors[0]?.$message.toString()"
  >
    <PvSelect
      v-model="model.gender"
      option-label="label"
      option-value="code"
      :options="Person.getGenderTypeList(true)"
      :disabled="isPrefilled('gender')"
    />
  </InputField>
</template>
